/**
 * Pure formatting helpers — no Vue reactivity, no i18n, no domain knowledge (structure.md's
 * `utils/`). Everything here is a number-to-string function whose output is read by a human and
 * never sent anywhere.
 */

const BinaryUnits = ['B', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB'] as const

/**
 * Bytes in binary units, because every source these numbers come from is binary: `Statfs` returns
 * blocks, `/proc/meminfo` reports KiB, and Docker's own output is binary throughout. Rendering a
 * `Statfs` figure in GB and a `df -h` figure in GiB for the same disk produces two different
 * numbers for one fact, and the operator reading both concludes the panel is wrong.
 *
 * The precision shrinks as the unit grows — `938 MiB`, `12.4 GiB`, `1.83 TiB` — so a meter label
 * stays one glance wide at every scale. A negative or non-finite input renders as an em dash
 * rather than as `NaN B`: the agent reports what the kernel gave it, and a missing reading is a
 * fact worth showing as missing.
 */
export const formatBytes = (bytes?: number | null): string => {
    if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return '—'
    if (bytes < 1024) return `${Math.round(bytes)} ${BinaryUnits[0]}`

    let value = bytes
    let unit  = 0

    while (value >= 1024 && unit < BinaryUnits.length - 1) {
        value /= 1024
        unit  += 1
    }

    const decimals = value >= 100 ? 0 : value >= 10 ? 1 : 2

    return `${value.toFixed(decimals)} ${BinaryUnits[unit]}`
}

/**
 * A percentage with one decimal below 10 and none above it, clamped to [0, 100].
 *
 * Clamped because a CPU delta sampled across a suspended VM can exceed 100 and a meter drawn past
 * its own track reads as a rendering bug rather than as a busy host.
 */
export const formatPercent = (value?: number | null): string => {
    if (value === null || value === undefined || !Number.isFinite(value)) return '—'

    const clamped = Math.min(100, Math.max(0, value))

    return `${clamped < 10 ? clamped.toFixed(1) : Math.round(clamped)} %`
}

/** The share of `total` that `used` occupies, as a 0–100 number. Zero total is zero, never `NaN`. */
export const usedPercent = (used: number, total: number): number =>
    total > 0 ? Math.min(100, Math.max(0, (used / total) * 100)) : 0

/** A load average with the two decimals `/proc/loadavg` itself carries. */
export const formatLoad = (value?: number | null): string =>
    value === null || value === undefined || !Number.isFinite(value) ? '—' : value.toFixed(2)

/**
 * The first twelve characters of a Docker id — what `docker ps` shows and what an operator will
 * paste into a command on the host. The full 64-character digest is never useful on screen.
 */
export const shortId = (id?: string | null): string => (id ?? '').slice(0, 12)

/**
 * A duration in seconds as `45 s`, `12 min`, `3 h 20 min`, `2 d 4 h` — at most two units, largest
 * first, the smaller one dropped when it is zero.
 *
 * # Why this lives in `utils/` despite being read by a human
 *
 * The unit symbols are **SI**, and SI symbols are the same string in both locales: Slovenian writes
 * `s`, `min`, `h` and `d` exactly as English does. So this stays a pure function with no `t()` in
 * it, and the sentence around it — *"offline for {duration}"* — is what the locale files translate
 * (§9.5). The alternative, spelled-out unit names, would need four plural forms per unit in sl for
 * no gain on a dashboard where compactness is the point.
 *
 * # Why it is not `dayjs().fromNow()`
 *
 * The relative-time formatter is deliberately lossy at exactly the sizes D60's thresholds live at:
 * it renders 45 seconds as "a minute" and 90 as "2 minutes", so *"threshold a minute"* would be
 * what an operator reads next to a number the panel is measuring to the second. `fromNow` is right
 * for a point in time (`since`, "3 hours ago") and wrong for a measured interval.
 *
 * A non-finite or absent input renders as an em dash: the value came off a customer's host, and a
 * missing reading is a fact worth showing as missing.
 *
 * A **negative** one is signed rather than rejected, because one of these durations is genuinely
 * bidirectional: clock drift is `sent_at − received_at`, and a site an hour behind is as broken as
 * one an hour ahead. Dropping the sign there would hide half the failure.
 */
export const formatDurationSeconds = (seconds?: number | null): string => {
    if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—'

    if (seconds < 0) return `-${formatDurationSeconds(-seconds)}`

    const total = Math.round(seconds)

    if (total < 60) return `${total} s`

    const minutes = Math.floor(total / 60)
    if (minutes < 60) return `${minutes} min`

    const hours = Math.floor(minutes / 60)
    if (hours < 24) {
        const rest = minutes % 60

        return rest > 0 ? `${hours} h ${rest} min` : `${hours} h`
    }

    const days = Math.floor(hours / 24)
    const rest = hours % 24

    return rest > 0 ? `${days} d ${rest} h` : `${days} d`
}
